import React, { useState, useCallback } from "react";
import { withTranslation } from "react-i18next";
import styles from "./VehicleSpecs.module.scss";
import sharedStyles from "../OrderDetailsNewVersion.module.scss";
import crStyles from "../../../customerReservation/crStyles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Grid from "@mui/material/Grid";
import { GetFloatToFixedValue } from "../../../../utils/GetFloatValue";
import {
  StyledBasicExpansionPanel,
  StyledBasicExpansionPanelSummary,
} from "../../../../shared/ExpansionPanels/ExpansionPanel";
import UpperCaseText from "../../../../utils/UpperCaseText";
import HasPermissionTo from "../../../../common/HasPermissionTo";
import {
  IS_AUS_MARKET,
  IS_THA_MARKET,
  IS_ZAF_MARKET,
} from "../../../../utils/EmpUtil";

const POSTFIX_CURRENCY_MARKETS = ['VNM', 'THA'];

function VehicleSpecs({
                        pricingStack,
                        defaultCurrency,
                        offlinePricingUpdate,
                        t,
                        nameplate,
                        customerReservation,
                        market,
                        user,
                      }) {
  const [expandSpecsInfo, setExpandSpecsInfo] = useState(true);
  const [expandAdditionalItems, setExpandAdditionalItems] = useState(true);

  const handleExpandCustomerInfo = useCallback(() => {
    setExpandSpecsInfo(prev => !prev);
  }, []);

  const handleExpandAdditionalItems = useCallback(() => {
    setExpandAdditionalItems(prev => !prev);
  }, []);

  const concatAccessoriesLists = useCallback((chargingAccessoriesData, ecommAccessoriesData) => {
    return chargingAccessoriesData.concat(ecommAccessoriesData);
  }, []);

  const commaSeparateAmount = useCallback((rawAmount) => {
    return rawAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, []);

  const specsRow = useCallback((styling, displayText, secondText, key = "") => {
    return (
      displayText && (
        <Grid container key={key === "" ? displayText : key}>
          <Grid item xs={10} className={styling}>
            <div>{displayText}</div>
          </Grid>
          <Grid item xs={2} className={styling}>
            {secondText && (
              <div className={styles.priceValue}>{secondText}</div>
            )}
          </Grid>
        </Grid>
      )
    );
  }, []);

  const formatPriceValue = useCallback((value, marketCode, hidePrice = false, precision = 2) => {
    if (hidePrice) {
      return "";
    }
    if (value !== undefined && value !== null) {
      if (marketCode === "THA") {
        precision = 0;
      }
      value = GetFloatToFixedValue(value, precision);
      value = value.toString();
      value = commaSeparateAmount(value);

      if (POSTFIX_CURRENCY_MARKETS.includes(marketCode)) {
        value =
          value.charAt(0) === "+" || value.charAt(0) === "-"
            ? value.charAt(0).concat(value.substr(1), " ", defaultCurrency)
            : value === "0"
              ? "--" // Show Dashes if amount is Zero
              : value.concat(" ", defaultCurrency); // Amount first then Currency Symbol
      } else {
        value =
          value.charAt(0) === "+" || value.charAt(0) === "-"
            ? value.charAt(0).concat(defaultCurrency, value.substr(1))
            : defaultCurrency.concat(value); // Currency Symbol show first and then Amount
      }
    } else {
      value = "--";
    }
    return value;
  }, [defaultCurrency, commaSeparateAmount]);

  const getSpecs = useCallback((specs, nameplate, details) => {
    if (details.vehicleFeaturesForIMG.AF8) {
      specs.push(details.vehicleFeaturesForIMG.AF8);
    }
    if (details.vehicleFeaturesForIMG.AF9) {
      specs.push(details.vehicleFeaturesForIMG.AF9);
    }
    let specsDesc = nameplate ? nameplate + ": " : "";
    specsDesc = specs && specsDesc + specs.join(" / ");
    return specsDesc;
  }, []);

  const getOTRPrice = useCallback((marketCode, data) => {
    let otrPrice = data.grossRetailPriceWithoutIncentives;
    if (
      IS_ZAF_MARKET(marketCode) &&
      data.grossRetailWithOTRWithAccessoriesAndAdditionalCharge
    ) {
      otrPrice = Math.round(
        data.grossRetailWithOTRWithAccessoriesAndAdditionalCharge,
        10
      );
    } else if (IS_AUS_MARKET(marketCode) && data.vehicleDriveAwayPrice) {
      otrPrice = Math.round(data.vehicleDriveAwayPrice, 10);
    } else if (IS_THA_MARKET(marketCode) && data.totalPriceWithAddOns) {
      otrPrice = Math.round(data.totalPriceWithAddOns, 10);
    } else if (data.finalGrossRetail) {
      otrPrice = data.finalGrossRetail;
    }
    return otrPrice;
  }, []);

  const data = pricingStack;

  return (
    <StyledBasicExpansionPanel
      expanded={
        offlinePricingUpdate.partialOffline
          ? false
          : expandSpecsInfo
      }
      disabled={offlinePricingUpdate.partialOffline}
      onChange={handleExpandCustomerInfo}
    >
      <StyledBasicExpansionPanelSummary
        style={crStyles.headerBarNeutral}
        id="exp"
      >
        {/* Vehicle Specs Accordion open/close */}
        <div className={sharedStyles.headerBar}>
          <div className={sharedStyles.boldHeader}>
            {offlinePricingUpdate.partialOffline
              ? t("CustomerOrderDetails.vehicleSpecsHeaderPricingAgreed")
              : t("CustomerOrderDetails.vehicleSpecsHeader")}
          </div>
          {!offlinePricingUpdate.partialOffline &&
            (expandSpecsInfo ? (
              <ExpandLessIcon id="less" />
            ) : (
              <ExpandMoreIcon id="more" />
            ))}
        </div>
        <br />
      </StyledBasicExpansionPanelSummary>

      <div className={styles.vehicleInfoGrid}>
        <div className={styles.bottomBorder} />

        {data ? (
          <>
            <Grid container className={styles.specsContainer}>
              {/* Gross Retail price, very first line */}
              {specsRow(
                styles.lightHeader,
                getSpecs(data.specs, nameplate, customerReservation),
                formatPriceValue(
                  data.grossRetail,
                  UpperCaseText(market),
                  market === "AUS" ? true : false
                )
              )}

              {/* Configured Options */}
              {specsRow(
                styles.darkHeader,
                t("VehicleSpecs.configureOptions"),
                formatPriceValue(
                  data.selectedFeaturePrice,
                  UpperCaseText(market)
                )
              )}

              {/* User Selected Features */}
              {data.userSelectedFeatures &&
                data.userSelectedFeatures.map((feature, index) =>
                  specsRow(
                    styles.darkIndentDetails,
                    feature.type,
                    formatPriceValue(
                      feature.value,
                      UpperCaseText(market)
                    ),
                    index
                  )
                )}

              {/* List of Dealer OTR Costs - no. of costs varies */}
              {data.dealerOTRCosts &&
                Object.keys(data.dealerOTRCosts).map((price) => {
                  return specsRow(
                    styles.darkIndentDetails,
                    t("VehicleSpecs." + price),
                    formatPriceValue(
                      data.dealerOTRCosts[price],
                      UpperCaseText(market)
                    )
                  );
                })}

              {/* Dealer Estimated Driveaway Price Total Price */}
              {specsRow(
                styles.lightHeader,
                market === "AUS"
                  ? t("VehicleSpecs.estimatedDriveawayPrice")
                  : t("VehicleSpecs.dealerOtrTotalPrice"),
                formatPriceValue(
                  getOTRPrice(UpperCaseText(market), data),
                  UpperCaseText(market),
                  false,
                  market === "AUS" ? 0 : 2
                )
              )}
            </Grid>

            <HasPermissionTo
              perform={["additionalItemsAccordion"]}
              permissions={user.permissions.rolePermissions}
              render={() => {
                return (
                  <StyledBasicExpansionPanel
                    defaultExpanded={true}
                    onChange={handleExpandAdditionalItems}
                  >
                    <StyledBasicExpansionPanelSummary
                      style={crStyles.headerBarNeutral}
                      id="exp"
                    >
                      <div className={styles.headerBar}>
                        <div>
                          {t("VehicleSpecs.additionalItems")}
                        </div>
                        {expandAdditionalItems ? (
                          <ExpandLessIcon id="less" />
                        ) : (
                          <ExpandMoreIcon id="more" />
                        )}
                      </div>
                      <br />
                    </StyledBasicExpansionPanelSummary>

                    {specsRow(
                      styles.darkHeader,
                      t("VehicleSpecs.accessories"),
                      formatPriceValue(
                        data.ecommAccessoriesPriceTotal,
                        UpperCaseText(market)
                      )
                    )}

                    {concatAccessoriesLists(
                      data.chargingAccessoriesData.accessoriesList,
                      data.ecommAccessoriesData
                    ).map((accessoriesOption) => {
                      return specsRow(
                        styles.darkIndentDetails,
                        accessoriesOption.shortDescription +
                        t("VehicleSpecs.partNo") +
                        accessoriesOption.code,
                        formatPriceValue(
                          accessoriesOption.dealerSellingPrice,
                          UpperCaseText(market)
                        )
                      );
                    })}
                  </StyledBasicExpansionPanel>
                );
              }}
            />
          </>
        ) : (
          <span className={styles.labelError}>
            {" "}
            {t("CustomerOrderDetails.errorLoadingDetails")}
          </span>
        )}
      </div>
    </StyledBasicExpansionPanel>
  );
}

export default withTranslation("emp")(VehicleSpecs);
